import { lazy, Suspense } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { FeatureFlagsProvider } from "@/contexts/FeatureFlagsContext";
import { LocaleProvider } from "@/contexts/LocaleContext";
// Lazy-load heavy layout components to keep radix-ui scroll-area,
// dropdown-menu, and sheet out of the initial JS bundle (unused on landing page)
const AppShell = lazy(() => import("@/components/layout/AppShell").then(m => ({ default: m.AppShell })));
// ConsentGate moved to AppShell — only shows in authenticated dashboard routes
import { ScrollToTop } from "@/components/ScrollToTop";
import { QuestionnairePromptProvider } from "@/components/questionnaire/QuestionnairePromptProvider";
import { RouteResolutionBoundary } from "@/components/routing/RouteResolutionBoundary";
import { PublicOnlyRoute } from "@/components/routing/PublicOnlyRoute";
import { AppShellLoading } from "@/components/routing/AppShellLoading";
import { LaunchSkeleton } from "@/components/launch/LaunchSkeleton";

// Lazy-load non-critical UI components to reduce main bundle and improve FCP
const Toaster = lazy(() => import("@/components/ui/toaster").then(m => ({ default: m.Toaster })));
const Sonner = lazy(() => import("@/components/ui/sonner").then(m => ({ default: m.Toaster })));
const PageTransitionLoader = lazy(() => import("@/components/PageTransitionLoader").then(m => ({ default: m.PageTransitionLoader })));
const AnalyticsConsentBanner = lazy(() => import("@/components/consent/AnalyticsConsentBanner").then(m => ({ default: m.AnalyticsConsentBanner })));

// Auth pages (lazy)
const Login = lazy(() => import("@/pages/auth/Login"));
const Signup = lazy(() => import("@/pages/auth/Signup"));
const ForgotPassword = lazy(() => import("@/pages/auth/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/auth/ResetPassword"));
const EmailConfirmed = lazy(() => import("@/pages/auth/EmailConfirmed"));
const OAuthCallback = lazy(() => import("@/pages/auth/OAuthCallback"));
const VerifyEmail = lazy(() => import("@/pages/auth/VerifyEmail"));
const PortalGate = lazy(() => import("@/pages/auth/PortalGate").then(m => ({ default: m.PortalGate })));

// Public pages (lazy)
const LandingPage = lazy(() => import("@/pages/public/LandingPage"));
const AboutPage = lazy(() => import("@/pages/public/AboutPage"));
const MissionPage = lazy(() => import("@/pages/public/MissionPage"));
const RoadmapPage = lazy(() => import("@/pages/public/RoadmapPage"));
const EducationHub = lazy(() => import("@/pages/public/education/EducationHub"));
const EducationCategory = lazy(() => import("@/pages/public/education/EducationCategory"));
const ContactPage = lazy(() => import("@/pages/public/ContactPage"));
const LegalPage = lazy(() => import("@/pages/public/LegalPage"));
const WaitlistWelcome = lazy(() => import("@/pages/public/WaitlistWelcome"));
const WaitlistStatus = lazy(() => import("@/pages/public/WaitlistStatus"));
const Waitlist2 = lazy(() => import("@/pages/public/Waitlist2"));
const ComingSoonPage = lazy(() => import("@/pages/public/ComingSoonPage"));
const ArticlesIndex = lazy(() => import("@/pages/public/ArticlesIndex"));
const ArticleDetail = lazy(() => import("@/pages/public/ArticleDetail"));
const UnionComparisonPage = lazy(() => import("@/pages/public/UnionComparisonPage"));
const WhatIsGigWorkerPage = lazy(() => import("@/pages/public/WhatIsGigWorkerPage"));
const FAQPage = lazy(() => import("@/pages/public/FAQPage"));
const LaunchPage = lazy(() => import("@/pages/public/LaunchPage"));
const DesignTokens = lazy(() => import("@/pages/public/DesignTokens"));
const Primitives = lazy(() => import("@/pages/public/Primitives"));

// User pages (lazy)
const UserHome = lazy(() => import("@/pages/user/Home"));
const UserProfile = lazy(() => import("@/pages/user/Profile"));
const UserMembership = lazy(() => import("@/pages/user/Membership"));
const UserBenefits = lazy(() => import("@/pages/user/Benefits"));
const UserEarnings = lazy(() => import("@/pages/user/Earnings"));
const UserWorkHistory = lazy(() => import("@/pages/user/WorkHistory"));
const UserRetirement = lazy(() => import("@/pages/user/Retirement"));
const UserInsurance = lazy(() => import("@/pages/user/Insurance"));
const UserPayments = lazy(() => import("@/pages/user/Payments"));
const UserDocuments = lazy(() => import("@/pages/user/Documents"));
const UserSupport = lazy(() => import("@/pages/user/Support"));
const UserSettings = lazy(() => import("@/pages/user/Settings"));
const UserSettingsBilling = lazy(() => import("@/pages/user/SettingsBilling"));
const UserReferrals = lazy(() => import("@/pages/user/Referrals"));
const UserPrivacy = lazy(() => import("@/pages/user/Privacy"));

// Admin pages (lazy)
const AdminHome = lazy(() => import("@/pages/admin/AdminHome"));
const AdminLogin = lazy(() => import("@/pages/admin/Login"));
const AcceptAdminInvite = lazy(() => import("@/pages/admin/AcceptInvite"));
const AdminUsers = lazy(() => import("@/pages/admin/Users"));
const AdminOrganizations = lazy(() => import("@/pages/admin/Organizations"));
const AdminMemberships = lazy(() => import("@/pages/admin/Memberships"));
const AdminBilling = lazy(() => import("@/pages/admin/Billing"));
const AdminReferrals = lazy(() => import("@/pages/admin/Referrals"));
const AdminBenefits = lazy(() => import("@/pages/admin/Benefits"));
const AdminInsurance = lazy(() => import("@/pages/admin/Insurance"));
const AdminRetirement = lazy(() => import("@/pages/admin/Retirement"));
const AdminContent = lazy(() => import("@/pages/admin/Content"));
const AdminContentList = lazy(() => import("@/pages/admin/ContentList"));
const AdminContentEditor = lazy(() => import("@/pages/admin/ContentEditor"));
const AdminContentPreview = lazy(() => import("@/pages/admin/ContentPreview"));
const AdminSupport = lazy(() => import("@/pages/admin/Support"));
const AdminCompliance = lazy(() => import("@/pages/admin/Compliance"));
const AdminCommunications = lazy(() => import("@/pages/admin/Communications"));
const AdminConsent = lazy(() => import("@/pages/admin/Consent"));
const AdminReports = lazy(() => import("@/pages/admin/Reports"));
const AdminFeatureFlags = lazy(() => import("@/pages/admin/FeatureFlags"));
const AdminAIGateway = lazy(() => import("@/pages/admin/AIGateway"));
const AdminSettings = lazy(() => import("@/pages/admin/Settings"));
const AdminBrandAssets = lazy(() => import("@/pages/admin/BrandAssets"));
const AdminQuestionnaire = lazy(() => import("@/pages/admin/Questionnaire"));
const AdminUserServices = lazy(() => import("@/pages/admin/UserServices"));
const AdminGigCompanies = lazy(() => import("@/pages/admin/GigCompanies"));
const AdminGigCategories = lazy(() => import("@/pages/admin/GigCategories"));
const AdminWorkTypes = lazy(() => import("@/pages/admin/WorkTypes"));
const AdminWorkTypeCategories = lazy(() => import("@/pages/admin/WorkTypeCategories"));
const AdminCMS = lazy(() => import("@/pages/admin/CMS"));
const AdminContentTopics = lazy(() => import("@/pages/admin/ContentTopics"));
const AdminWaitlist = lazy(() => import("@/pages/admin/Waitlist"));
const AdminLaunchWaitlist = lazy(() => import("@/pages/admin/LaunchWaitlist"));
const AdminAnalytics = lazy(() => import("@/pages/admin/Analytics"));
const AdminAnalyticsSchema = lazy(() => import("@/pages/admin/AnalyticsSchema"));
const AdminSecurityLogs = lazy(() => import("@/pages/admin/SecurityLogs"));
const AdminKillSwitch = lazy(() => import("@/pages/admin/KillSwitch"));
const AdminRevenueForecast = lazy(() => import("@/pages/admin/RevenueForecast"));
const AdminKPIDashboard = lazy(() => import("@/pages/admin/kpi/KPIDashboard"));
const AdminReportBuilder = lazy(() => import("@/pages/admin/kpi/ReportBuilder"));
const AdminScheduledReports = lazy(() => import("@/pages/admin/kpi/ScheduledReports"));
const AdminSystemReset = lazy(() => import("@/pages/admin/SystemReset"));
const AdminFoundingMembers = lazy(() => import("@/pages/admin/FoundingMembers"));
const AdminEarlyAccessCountdown = lazy(() => import("@/pages/admin/EarlyAccessCountdown"));

// AdiGig pages (lazy)
const AdigigOverview = lazy(() => import("@/pages/adigig/Overview"));
const AdigigUsers = lazy(() => import("@/pages/adigig/Users"));
const AdigigSessions = lazy(() => import("@/pages/adigig/Sessions"));
const AdigigMap = lazy(() => import("@/pages/adigig/Map"));
const AdigigConsent = lazy(() => import("@/pages/adigig/Consent"));
const AdigigDataQuality = lazy(() => import("@/pages/adigig/DataQuality"));
const AdigigInsights = lazy(() => import("@/pages/adigig/Insights"));
const AdigigExports = lazy(() => import("@/pages/adigig/Exports"));
const AdigigIntegrations = lazy(() => import("@/pages/adigig/Integrations"));
const AdigigRetention = lazy(() => import("@/pages/adigig/Retention"));
const AdigigStatus = lazy(() => import("@/pages/adigig/Status"));
const AdigigSettings = lazy(() => import("@/pages/adigig/Settings"));
const AdigigSignup = lazy(() => import("@/pages/adigig/Signup"));
const AdigigLogin = lazy(() => import("@/pages/adigig/Login"));
const AdigigPending = lazy(() => import("@/pages/adigig/Pending"));

const FoundingMembersPage = lazy(() => import("@/pages/public/FoundingMembersPage"));
const PortableBenefitsLandingPage = lazy(() => import("@/pages/public/PortableBenefitsLandingPage"));
const CollectiveAccessLandingPage = lazy(() => import("@/pages/public/CollectiveAccessLandingPage"));
const WorkerModelLandingPage = lazy(() => import("@/pages/public/WorkerModelLandingPage"));
const SeoLandingPage = lazy(() => import("@/pages/public/SeoLandingPage"));
const SeoLandingPreview = lazy(() => import("@/pages/admin/SeoLandingPreview"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const Unauthorized = lazy(() => import("@/pages/Unauthorized"));
const AccountDeleted = lazy(() => import("@/pages/AccountDeleted"));
const MembershipPaywall = lazy(() => import("@/pages/MembershipPaywall"));

// Community pages (lazy)
const CommunityHome = lazy(() => import("@/pages/community/CommunityHome"));

// Onboarding pages (lazy)
const OnboardingProfile = lazy(() => import("@/pages/onboarding/Profile"));
const OnboardingMembership = lazy(() => import("@/pages/onboarding/Membership"));
const OnboardingQuestionnaire = lazy(() => import("@/pages/onboarding/Questionnaire"));
const OnboardingComplete = lazy(() => import("@/pages/onboarding/Complete"));

const queryClient = new QueryClient();

const RouteFallback = () => {
  const isLaunchRoute = typeof window !== "undefined" && ["/", "/launch"].includes(window.location.pathname);
  return isLaunchRoute ? <LaunchSkeleton /> : <AppShellLoading />;
};

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ThemeProvider>
          <LocaleProvider>
            <TooltipProvider>
              <Suspense fallback={null}><Toaster /></Suspense>
              <Suspense fallback={null}><Sonner /></Suspense>
              <FeatureFlagsProvider>
                    <Suspense fallback={null}><AnalyticsConsentBanner /></Suspense>
                    <BrowserRouter>
                      <ScrollToTop />
                      <Suspense fallback={null}><PageTransitionLoader /></Suspense>
                      <QuestionnairePromptProvider>
                      <RouteResolutionBoundary>
                      <Suspense fallback={<RouteFallback />}>
                      <Routes>
                        {/* Public marketing pages */}
                        <Route path="/" element={<Suspense fallback={<LaunchSkeleton />}><LaunchPage /></Suspense>} />
                        <Route path="/home" element={<LandingPage />} />
                        <Route path="/about" element={<AboutPage />} />
                        <Route path="/mission" element={<MissionPage />} />
                        <Route path="/roadmap" element={<RoadmapPage />} />
                        <Route path="/education" element={<EducationHub />} />
                        <Route path="/union-vs-adigo" element={<UnionComparisonPage />} />
                        <Route path="/what-is-a-gig-worker" element={<WhatIsGigWorkerPage />} />
                        <Route path="/education/:category" element={<EducationCategory />} />
                        <Route path="/contact" element={<ContactPage />} />
                        <Route path="/legal/:type" element={<LegalPage />} />
                        <Route path="/waitlist" element={<WaitlistWelcome />} />
                        <Route path="/waitlist/status" element={<WaitlistStatus />} />
                        <Route path="/waitlist2" element={<Waitlist2 />} />
                        <Route path="/launch" element={<Suspense fallback={<LaunchSkeleton />}><LaunchPage /></Suspense>} />
                        <Route path="/faq" element={<FAQPage />} />
                        <Route path="/coming-soon/:page" element={<ComingSoonPage />} />
                        <Route path="/design-tokens" element={<DesignTokens />} />
                        <Route path="/primitives" element={<Primitives />} />
                        
                        {/* Public articles/content hub */}
                        <Route path="/articles" element={<ArticlesIndex />} />
                        <Route path="/articles/:slug" element={<ArticleDetail />} />
                        <Route path="/press" element={<Navigate to="/articles?type=press_release" replace />} />
                        <Route path="/statements" element={<Navigate to="/articles?type=statement" replace />} />
                        
                        {/* Public auth routes - User/Member */}
                        <Route path="/login" element={<PublicOnlyRoute><Login /></PublicOnlyRoute>} />
                        <Route path="/signup" element={<PublicOnlyRoute><Signup /></PublicOnlyRoute>} />
                        <Route path="/forgot-password" element={<PublicOnlyRoute><ForgotPassword /></PublicOnlyRoute>} />
                        <Route path="/reset-password" element={<ResetPassword />} />
                        <Route path="/auth/confirm" element={<EmailConfirmed />} />
                        <Route path="/auth/oauth-callback" element={<OAuthCallback />} />
                        <Route path="/verify-email" element={<VerifyEmail />} />
                        
                        {/* Public membership paywall */}
                        <Route path="/pricing" element={<MembershipPaywall />} />
                        
                        {/* Founding Members routes */}
                        <Route path="/founding-members" element={<FoundingMembersPage variant="shared" />} />
                        <Route path="/founding-members-a" element={<FoundingMembersPage variant="a" />} />
                        <Route path="/founding-members-b" element={<FoundingMembersPage variant="b" />} />

                        {/* Campaign 1 proposition landing experiences */}
                        <Route path="/p/portable-benefits" element={<PortableBenefitsLandingPage />} />
                        <Route path="/p/collective-access" element={<CollectiveAccessLandingPage />} />
                        <Route path="/p/worker-model" element={<WorkerModelLandingPage />} />

                        {/* Community routes (feature-flagged) */}
                        <Route path="/community" element={<CommunityHome />} />
                        
                        {/* Onboarding routes */}
                        <Route path="/onboarding/profile" element={<OnboardingProfile />} />
                        <Route path="/onboarding/membership" element={<OnboardingMembership />} />
                        <Route path="/onboarding/questionnaire" element={<OnboardingQuestionnaire />} />
                        <Route path="/onboarding/complete" element={<OnboardingComplete />} />
                        
                        {/* AdiGig public routes - old paths neutralized (redirect to home) */}
                        <Route path="/adigig/login" element={<Navigate to="/" replace />} />
                        <Route path="/adigig/signup" element={<AdigigSignup />} />
                        <Route path="/adigig/pending" element={<AdigigPending />} />
                        
                        {/* Admin public routes - old path neutralized (redirect to home) */}
                        <Route path="/admin/login" element={<Navigate to="/" replace />} />
                        <Route path="/admin/accept-invite" element={<AcceptAdminInvite />} />
                        
                        {/* Token-gated portal entry routes */}
                        <Route path="/admin-access/:token" element={<PortalGate portal="admin"><AdminLogin /></PortalGate>} />
                        <Route path="/adigig-access/:token" element={<PortalGate portal="adigig"><AdigigLogin /></PortalGate>} />
                        
                        {/* Unauthorized page */}
                        <Route path="/unauthorized" element={<Unauthorized />} />
                        
                        {/* Account deleted success page */}
                        <Route path="/account-deleted" element={<AccountDeleted />} />
                        
                        {/* User Dashboard Routes - user role only */}
                        <Route element={<AppShell allowedRoles={["user"]} />}>
                          <Route path="/dashboard" element={<UserHome />} />
                          <Route path="/profile" element={<UserProfile />} />
                          <Route path="/membership" element={<UserMembership />} />
                          <Route path="/benefits" element={<UserBenefits />} />
                          <Route path="/earnings" element={<UserEarnings />} />
                          <Route path="/work-history" element={<UserWorkHistory />} />
                          <Route path="/retirement" element={<UserRetirement />} />
                          <Route path="/insurance" element={<UserInsurance />} />
                          <Route path="/payments" element={<UserPayments />} />
                          <Route path="/documents" element={<UserDocuments />} />
                          <Route path="/support" element={<UserSupport />} />
                          <Route path="/settings" element={<UserSettings />} />
                          <Route path="/settings/billing" element={<UserSettingsBilling />} />
                          <Route path="/referrals" element={<UserReferrals />} />
                          <Route path="/privacy" element={<UserPrivacy />} />
                        </Route>

                        {/* Admin Dashboard Routes - admin only */}
                        <Route element={<AppShell allowedRoles={["admin"]} />}>
                          <Route path="/admin" element={<AdminHome />} />
                          <Route path="/admin/users" element={<AdminUsers />} />
                          <Route path="/admin/organizations" element={<AdminOrganizations />} />
                          <Route path="/admin/gig-companies" element={<AdminGigCompanies />} />
                          <Route path="/admin/work-types" element={<AdminWorkTypes />} />
                          <Route path="/admin/work-type-categories" element={<AdminWorkTypeCategories />} />
                          <Route path="/admin/gig-categories" element={<AdminGigCategories />} />
                          <Route path="/admin/memberships" element={<AdminMemberships />} />
                          <Route path="/admin/billing" element={<AdminBilling />} />
                          <Route path="/admin/referrals" element={<AdminReferrals />} />
                          <Route path="/admin/benefits" element={<AdminBenefits />} />
                          <Route path="/admin/insurance" element={<AdminInsurance />} />
                          <Route path="/admin/retirement" element={<AdminRetirement />} />
                          <Route path="/admin/content" element={<AdminContentList />} />
                          <Route path="/admin/content/new" element={<AdminContentEditor />} />
                          <Route path="/admin/content/:id/edit" element={<AdminContentEditor />} />
                          <Route path="/admin/content/:id/preview" element={<AdminContentPreview />} />
                          <Route path="/admin/content/topics" element={<AdminContentTopics />} />
                          <Route path="/admin/support" element={<AdminSupport />} />
                          <Route path="/admin/compliance" element={<AdminCompliance />} />
                          <Route path="/admin/communications" element={<AdminCommunications />} />
                          <Route path="/admin/consent" element={<AdminConsent />} />
                          <Route path="/admin/reports" element={<AdminReports />} />
                          <Route path="/admin/feature-flags" element={<AdminFeatureFlags />} />
                          <Route path="/admin/ai-gateway" element={<AdminAIGateway />} />
                          <Route path="/admin/settings" element={<AdminSettings />} />
                          <Route path="/admin/brand-assets" element={<AdminBrandAssets />} />
                          <Route path="/admin/questionnaire" element={<AdminQuestionnaire />} />
                          <Route path="/admin/user-services" element={<AdminUserServices />} />
                          <Route path="/admin/cms" element={<AdminCMS />} />
                          <Route path="/admin/waitlist" element={<AdminWaitlist />} />
                          <Route path="/admin/launch-waitlist" element={<AdminLaunchWaitlist />} />
                          <Route path="/admin/analytics" element={<AdminAnalytics />} />
                          <Route path="/admin/analytics/schema" element={<AdminAnalyticsSchema />} />
                          <Route path="/admin/security/logs" element={<AdminSecurityLogs />} />
                          <Route path="/admin/kill-switch" element={<AdminKillSwitch />} />
                          <Route path="/admin/billing/forecast" element={<AdminRevenueForecast />} />
                          <Route path="/admin/kpi/dashboard" element={<AdminKPIDashboard />} />
                          <Route path="/admin/kpi/reports/new" element={<AdminReportBuilder />} />
                          <Route path="/admin/kpi/reports/scheduled" element={<AdminScheduledReports />} />
                          <Route path="/admin/system-reset" element={<AdminSystemReset />} />
                          <Route path="/admin/founding-members" element={<AdminFoundingMembers />} />
                          <Route path="/admin/waitlist/countdown" element={<AdminEarlyAccessCountdown />} />
                        </Route>

                        {/* AdiGig Dashboard Routes - adigig only */}
                        <Route element={<AppShell allowedRoles={["adigig"]} />}>
                          <Route path="/adigig" element={<AdigigOverview />} />
                          <Route path="/adigig/users" element={<AdigigUsers />} />
                          <Route path="/adigig/sessions" element={<AdigigSessions />} />
                          <Route path="/adigig/map" element={<AdigigMap />} />
                          <Route path="/adigig/consent" element={<AdigigConsent />} />
                          <Route path="/adigig/data-quality" element={<AdigigDataQuality />} />
                          <Route path="/adigig/insights" element={<AdigigInsights />} />
                          <Route path="/adigig/exports" element={<AdigigExports />} />
                          <Route path="/adigig/integrations" element={<AdigigIntegrations />} />
                          <Route path="/adigig/retention" element={<AdigigRetention />} />
                          <Route path="/adigig/status" element={<AdigigStatus />} />
                          <Route path="/adigig/settings" element={<AdigigSettings />} />
                        </Route>

                        {/* SEO Landing preview (admin-gated in component) */}
                        <Route path="/preview/seo/:id" element={<SeoLandingPreview />} />

                        {/* SEO Landing public pages — must be BEFORE catch-all */}
                        <Route path="/:slug" element={<SeoLandingPage />} />

                        <Route path="*" element={<NotFound />} />
                      </Routes>
                      </Suspense>
                      </RouteResolutionBoundary>
                      </QuestionnairePromptProvider>
                    </BrowserRouter>
              </FeatureFlagsProvider>
            </TooltipProvider>
          </LocaleProvider>
        </ThemeProvider>
      </AuthProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
